import { GridApiClient } from "../api/grid-api-client";
import {
  AuthorizationError,
  GridError,
  UpdateAccountRequest,
  GetAccountBalancesQueryParams,
  GetTransfersOptions,
  AccountBalancesData,
  TransferResponse,
  GetAccountResponse,
  AccountBalancesResponse,
  GetTransactionsResponse,
} from "../types";
import {
  GridClientContext,
} from "../types/common";
import { TransactionPayload, TransactionResponse } from "../managers/signing-manager";

/**
 * @hidden
 * Service handling account management operations
 */
export class AccountService {
  private apiClient: GridApiClient;
  private context: GridClientContext;

  constructor(apiClient: GridApiClient, context: GridClientContext) {
    this.apiClient = apiClient;
    this.context = context;
  }

  /**
   * Retrieves comprehensive account details including account type, policies,
   * signers, thresholds, and metadata.
   *
   * @param accountAddress - The account address to retrieve information for
   * @returns Promise resolving to account details
   *
   * @example
   * ```typescript
   * try {
   *   const account = await gridClient.getAccount('account-address');
   *   console.log('Account type:', account.type);
   *   console.log('Signers:', account.policies?.signers);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to get account:', error.message);
   *   }
   * }
   * ```
   *
   * @category Account Management
   */
  async getAccount(accountAddress: string): Promise<GetAccountResponse> {
    const response = await this.apiClient.getAccount(accountAddress);
    return response;
  }

  /**
   * Update account settings and configuration such as policies, signers, thresholds, and metadata.
   *
   * @param accountAddress - The account address to update
   * @param request - Update request containing the fields to modify
   * @param admin - Whether to perform the update with admin privileges (optional)
   * @returns Promise resolving to transaction payload for the update operation
   *
   * @example
   * ```typescript
   * try {
   *   const updateTx = await gridClient.updateAccount(accountAddress, {
   *     threshold: 2
   *   });
   *   console.log('Update transaction:', updateTx.transaction);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to update account:', error.message);
   *   }
   * }
   * ```
   *
   * @example Admin update
   * ```typescript
   * const adminUpdateTx = await gridClient.updateAccount(
   *   accountAddress,
   *   { threshold: 1 },
   *   true
   * );
   * ```
   *
   * @category Account Management
   */
  async updateAccount(
    accountAddress: string,
    request: UpdateAccountRequest,
    admin?: boolean
  ): Promise<TransactionResponse> {
    const response = await this.apiClient.updateAccount(
      accountAddress,
      request,
      admin
    );
    return response;
  }

  /**
   * Get account token balances
   *
   * Retrieves the current token balances for an account, including both
   * native SOL and SPL token balances. Supports filtering and pagination.
   *
   * @param accountAddress - The account address to get balances for
   * @param queryParams - Optional query parameters for filtering and pagination
   * @returns Promise resolving to account balances data
   *
   * @example
   * ```typescript
   * try {
   *   const balances = await gridClient.getAccountBalances(accountAddress);
   *   console.log('SOL balance:', balances.sol);
   *   console.log('Token balances:', balances.tokens);
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to get balances:', error.message);
   *   }
   * }
   * ```
   *
   * @category Account Management
   */
  async getAccountBalances(
    accountAddress: string,
    queryParams?: GetAccountBalancesQueryParams
  ): Promise<AccountBalancesResponse> {
    const response = await this.apiClient.getAccountBalances(
      accountAddress,
      queryParams
    );

    return response;
  }

  /**
   * Retrieves the transaction history for an account, including incoming and
   * outgoing transfers. Supports filtering by date range, token type, and pagination.
   *
   * @param accountAddress - The account address to get transfers for
   * @param options - Optional filtering and pagination options
   * @returns Promise resolving to array of transfer data
   *
   * @example
   * ```typescript
   * try {
   *   const transfers = await gridClient.getTransfers(accountAddress);
   *   transfers.forEach(transfer => {
   *     console.log('Transfer:', transfer.id);
   *   });
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to get transfers:', error.message);
   *   }
   * }
   * ```
   *
   * @example With filtering
   * ```typescript
   * const transfers = await gridClient.getTransfers(accountAddress, {
   *   limit: 10,
   *   currency: 'usd'
   * });
   * ```
   *
   * @category Account Management
   */
  async getTransfers(
    accountAddress: string,
    options?: GetTransfersOptions
  ): Promise<TransferResponse> {
    const response = await this.apiClient.getTransfers(
      accountAddress,
      options
    );
    return response;
  }

  /**
   * Retrieves the blockchain transaction history for an account
   *
   * Returns a list of Solana blockchain transactions associated with the account,
   * including transaction signatures, statuses, and timestamps.
   *
   * @param accountAddress - The account address to get transactions for
   * @returns Promise resolving to transaction history data
   *
   * @example
   * ```typescript
   * try {
   *   const response = await gridClient.getTransactions(accountAddress);
   *   if (response.data?.transactions) {
   *     response.data.transactions.forEach(tx => {
   *       console.log('Transaction:', tx.signature);
   *       console.log('Status:', tx.status);
   *       console.log('Created:', tx.createdAt);
   *     });
   *   }
   * } catch (error) {
   *   if (error instanceof ApiError) {
   *     console.error('Failed to get transactions:', error.message);
   *   }
   * }
   * ```
   *
   * @category Account Management
   */
  async getTransactions(accountAddress: string): Promise<GetTransactionsResponse> {
    const response = await this.apiClient.getTransactions(accountAddress);
    return response;
  }
}
